export type HotelAmenity =
  | "Wi-Fi"
  | "Pool"
  | "Parking"
  | "Breakfast"
  | "Air conditioning"
  | "Spa"
  | "Gym"
  | "Restaurant"
  | "Beach access"
  | "Free cancellation";

export type PropertyType =
  | "Resort"
  | "Boutique"
  | "Business"
  | "Luxury"
  | "Heritage"
  | "Lodge";

export type RoomOption = {
  id: string;
  name: string;
  image: string;
  occupancy: number;
  beds: string;
  amenities: string[];
  mealPlan: string;
  cancellation: string;
  pricePerNight: number;
  taxesPerNight: number;
  memberDiscountPct: number;
  pointsEarn: number;
};

export type MockHotel = {
  id: string;
  name: string;
  city: string;
  state: string;
  country: string;
  rating: number;
  reviews: number;
  stars: number;
  propertyType: PropertyType;
  tagline: string;
  description: string;
  amenities: HotelAmenity[];
  pricePerNight: number;
  taxesNote: string;
  memberDiscountPct: number;
  pointsEarnUpTo: number;
  memberExclusive: boolean;
  freeCancellation: boolean;
  breakfastIncluded: boolean;
  image: string;
  gallery: string[];
  checkIn: string;
  checkOut: string;
  highlights: string[];
  rooms: RoomOption[];
  categoryTags: string[];
};

export type Destination = {
  id: string;
  name: string;
  city: string;
  label: string;
  image: string;
  hotelCount: number;
};

export const MOCK_DESTINATIONS: Destination[] = [
  {
    id: "dest-goa",
    name: "Goa",
    city: "Goa",
    label: "Beach escapes",
    image:
      "https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?auto=format&fit=crop&w=900&q=80",
    hotelCount: 1,
  },
  {
    id: "dest-mumbai",
    name: "Mumbai",
    city: "Mumbai",
    label: "Business travel",
    image:
      "https://images.unsplash.com/photo-1529253355930-ddbe423a2ac7?auto=format&fit=crop&w=900&q=80",
    hotelCount: 1,
  },
  {
    id: "dest-jaipur",
    name: "Jaipur",
    city: "Jaipur",
    label: "Heritage stays",
    image:
      "https://images.unsplash.com/photo-1477587458883-47145ed94245?auto=format&fit=crop&w=900&q=80",
    hotelCount: 1,
  },
];

function room(
  id: string,
  name: string,
  image: string,
  occupancy: number,
  beds: string,
  pricePerNight: number,
  memberDiscountPct: number,
  pointsEarn: number,
  extras: Partial<RoomOption> = {},
): RoomOption {
  return {
    id,
    name,
    image,
    occupancy,
    beds,
    amenities: ["Wi-Fi", "Air conditioning", "Room service"],
    mealPlan: "Room only",
    cancellation: "Free cancellation until 24h before check-in",
    pricePerNight,
    taxesPerNight: Math.round(pricePerNight * 0.12),
    memberDiscountPct,
    pointsEarn,
    ...extras,
  };
}

/** Demo stay catalog for the member travel portal. */
export const MOCK_HOTELS: MockHotel[] = [
  {
    id: "htl-1",
    name: "Azure Bay Resort & Spa",
    city: "Goa",
    state: "Goa",
    country: "India",
    rating: 4.8,
    reviews: 1240,
    stars: 5,
    propertyType: "Resort",
    tagline: "Oceanfront suites with private pool decks",
    description:
      "A coastal resort with palm-lined pathways, infinity pools, and spa pavilions overlooking the Arabian Sea.",
    amenities: [
      "Wi-Fi",
      "Pool",
      "Spa",
      "Breakfast",
      "Beach access",
      "Parking",
      "Air conditioning",
      "Free cancellation",
    ],
    pricePerNight: 8499,
    taxesNote: "Taxes & fees in points extra",
    memberDiscountPct: 12,
    pointsEarnUpTo: 850,
    memberExclusive: true,
    freeCancellation: true,
    breakfastIncluded: true,
    image:
      "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1400&q=80",
    gallery: [
      "https://images.unsplash.com/photo-1582719508461-905c673771fd?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1571896349842-33c89424de2d?auto=format&fit=crop&w=1200&q=80",
    ],
    checkIn: "2:00 PM",
    checkOut: "11:00 AM",
    highlights: ["Private beach access", "Infinity pool", "Spa credit for Gold+"],
    categoryTags: ["popular", "luxury", "recommended", "value"],
    rooms: [
      room(
        "htl-1-deluxe",
        "Deluxe Ocean View",
        "https://images.unsplash.com/photo-1618773928122-2b09bfd4f4cb?auto=format&fit=crop&w=900&q=80",
        2,
        "1 King bed",
        8499,
        12,
        850,
        { mealPlan: "Breakfast included" },
      ),
      room(
        "htl-1-suite",
        "Azure Pool Suite",
        "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?auto=format&fit=crop&w=900&q=80",
        3,
        "1 King + sofa bed",
        12999,
        15,
        1300,
        { mealPlan: "Breakfast included", amenities: ["Private pool", "Wi-Fi", "Butler"] },
      ),
    ],
  },
  {
    id: "htl-2",
    name: "The Crown Heights",
    city: "Mumbai",
    state: "Maharashtra",
    country: "India",
    rating: 4.6,
    reviews: 890,
    stars: 5,
    propertyType: "Business",
    tagline: "Skyline views in the heart of the city",
    description:
      "A polished business hotel near BKC with club lounges and fast airport transfers.",
    amenities: [
      "Wi-Fi",
      "Gym",
      "Restaurant",
      "Parking",
      "Air conditioning",
      "Breakfast",
    ],
    pricePerNight: 7299,
    taxesNote: "Taxes & fees in points extra",
    memberDiscountPct: 8,
    pointsEarnUpTo: 730,
    memberExclusive: false,
    freeCancellation: true,
    breakfastIncluded: false,
    image:
      "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?auto=format&fit=crop&w=1400&q=80",
    gallery: [
      "https://images.unsplash.com/photo-1618773928122-2b09bfd4f4cb?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?auto=format&fit=crop&w=1200&q=80",
    ],
    checkIn: "3:00 PM",
    checkOut: "12:00 PM",
    highlights: ["Club lounge", "Airport desk", "Meeting rooms"],
    categoryTags: ["popular", "recommended", "business"],
    rooms: [
      room(
        "htl-2-std",
        "City Deluxe",
        "https://images.unsplash.com/photo-1590490360182-c33d57733427?auto=format&fit=crop&w=900&q=80",
        2,
        "1 King or Twin",
        7299,
        8,
        730,
      ),
      room(
        "htl-2-exec",
        "Executive Club Room",
        "https://images.unsplash.com/photo-1578683010236-d716f9a3f461?auto=format&fit=crop&w=900&q=80",
        2,
        "1 King bed",
        9899,
        10,
        990,
        { mealPlan: "Club breakfast" },
      ),
    ],
  },
  {
    id: "htl-3",
    name: "The Grand Palace",
    city: "Jaipur",
    state: "Rajasthan",
    country: "India",
    rating: 4.6,
    reviews: 1024,
    stars: 5,
    propertyType: "Heritage",
    tagline: "Courtyard luxury in pink-city splendour",
    description:
      "A heritage-inspired palace hotel with carved arches, courtyard dining, and cultural evenings.",
    amenities: [
      "Wi-Fi",
      "Pool",
      "Spa",
      "Restaurant",
      "Breakfast",
      "Parking",
      "Air conditioning",
      "Free cancellation",
    ],
    pricePerNight: 5499,
    taxesNote: "Taxes & fees in points extra",
    memberDiscountPct: 10,
    pointsEarnUpTo: 500,
    memberExclusive: true,
    freeCancellation: true,
    breakfastIncluded: true,
    image:
      "https://images.unsplash.com/photo-1564501049412-61c2a3083791?auto=format&fit=crop&w=1400&q=80",
    gallery: [
      "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1590490360182-c33d57733427?auto=format&fit=crop&w=1200&q=80",
    ],
    checkIn: "2:00 PM",
    checkOut: "11:00 AM",
    highlights: ["Heritage courtyard", "Cultural evenings", "Member rate"],
    categoryTags: ["recommended", "luxury", "value", "popular"],
    rooms: [
      room(
        "htl-3-prem",
        "Palace Premium",
        "https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?auto=format&fit=crop&w=900&q=80",
        2,
        "1 King bed",
        5499,
        10,
        500,
        { mealPlan: "Breakfast included" },
      ),
      room(
        "htl-3-suite",
        "Maharaja Suite",
        "https://images.unsplash.com/photo-1584132967334-10e028bd69f7?auto=format&fit=crop&w=900&q=80",
        3,
        "1 King + daybed",
        8999,
        12,
        900,
        { mealPlan: "Half board" },
      ),
    ],
  },
  {
    id: "htl-4",
    name: "The Orchard Inn",
    city: "Coorg",
    state: "Karnataka",
    country: "India",
    rating: 4.4,
    reviews: 612,
    stars: 4,
    propertyType: "Lodge",
    tagline: "Coffee-estate cottages above the valley",
    description:
      "A quiet lodge set in a working coffee estate, with verandahs, bird calls, and slow breakfasts.",
    amenities: ["Wi-Fi", "Parking", "Restaurant", "Air conditioning", "Free cancellation"],
    pricePerNight: 4299,
    taxesNote: "Taxes calculated at checkout",
    memberDiscountPct: 8,
    pointsEarnUpTo: 430,
    memberExclusive: false,
    freeCancellation: true,
    breakfastIncluded: false,
    image:
      "https://images.unsplash.com/photo-1445019980597-93fa8acb246c?auto=format&fit=crop&w=1400&q=80",
    gallery: [
      "https://images.unsplash.com/photo-1501117716987-c8e1ecb210d0?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?auto=format&fit=crop&w=1200&q=80",
    ],
    checkIn: "1:00 PM",
    checkOut: "11:00 AM",
    highlights: ["Estate walks", "Valley view", "Member rate"],
    categoryTags: ["value"],
    rooms: [
      room(
        "htl-4-cottage",
        "Estate Cottage",
        "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=900&q=80",
        2,
        "1 Queen bed",
        4299,
        8,
        430,
      ),
      room(
        "htl-4-suite",
        "Plantation Suite",
        "https://images.unsplash.com/photo-1616594039964-ae9021a400a0?auto=format&fit=crop&w=900&q=80",
        3,
        "1 King bed",
        6899,
        10,
        690,
        { mealPlan: "Breakfast included" },
      ),
    ],
  },
  {
    id: "htl-5",
    name: "Skyline Business Hotel",
    city: "Bengaluru",
    state: "Karnataka",
    country: "India",
    rating: 4.2,
    reviews: 890,
    stars: 4,
    propertyType: "Business",
    tagline: "A practical base beside the tech corridor",
    description:
      "A city hotel with work desks, a gym, and a quick airport link for short business trips.",
    amenities: ["Wi-Fi", "Gym", "Parking", "Air conditioning", "Restaurant"],
    pricePerNight: 3899,
    taxesNote: "Taxes calculated at checkout",
    memberDiscountPct: 6,
    pointsEarnUpTo: 390,
    memberExclusive: false,
    freeCancellation: false,
    breakfastIncluded: false,
    image:
      "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?auto=format&fit=crop&w=1400&q=80",
    gallery: [
      "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1590490360182-c33d57733427?auto=format&fit=crop&w=1200&q=80",
    ],
    checkIn: "2:00 PM",
    checkOut: "12:00 PM",
    highlights: ["Airport link", "Work desk", "Gym"],
    categoryTags: ["value"],
    rooms: [
      room(
        "htl-5-std",
        "City Room",
        "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?auto=format&fit=crop&w=900&q=80",
        2,
        "1 Queen bed",
        3899,
        6,
        390,
        { cancellation: "Non-refundable" },
      ),
      room(
        "htl-5-exec",
        "Executive Room",
        "https://images.unsplash.com/photo-1590490360182-c33d57733427?auto=format&fit=crop&w=900&q=80",
        2,
        "1 King bed",
        5299,
        8,
        530,
        { mealPlan: "Breakfast included", cancellation: "Non-refundable" },
      ),
    ],
  },
  {
    id: "htl-6",
    name: "Lake Pavilion",
    city: "Udaipur",
    state: "Rajasthan",
    country: "India",
    rating: 4.7,
    reviews: 743,
    stars: 5,
    propertyType: "Luxury",
    tagline: "Courtyard rooms looking onto the lake",
    description:
      "A lakeside hotel with arched courtyards, a small pool, and evening boat views.",
    amenities: [
      "Wi-Fi",
      "Pool",
      "Spa",
      "Breakfast",
      "Restaurant",
      "Air conditioning",
      "Free cancellation",
    ],
    pricePerNight: 9799,
    taxesNote: "Taxes calculated at checkout",
    memberDiscountPct: 12,
    pointsEarnUpTo: 980,
    memberExclusive: true,
    freeCancellation: true,
    breakfastIncluded: true,
    image:
      "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?auto=format&fit=crop&w=1400&q=80",
    gallery: [
      "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?auto=format&fit=crop&w=1200&q=80",
      "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1200&q=80",
    ],
    checkIn: "3:00 PM",
    checkOut: "11:00 AM",
    highlights: ["Lake view", "Courtyard pool", "Member rate"],
    categoryTags: ["luxury", "popular"],
    rooms: [
      room(
        "htl-6-lake",
        "Lake View Room",
        "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=900&q=80",
        2,
        "1 King bed",
        9799,
        12,
        980,
        { mealPlan: "Breakfast included" },
      ),
      room(
        "htl-6-suite",
        "Pavilion Suite",
        "https://images.unsplash.com/photo-1618773928122-2b09bfd4f4cb?auto=format&fit=crop&w=900&q=80",
        3,
        "1 King + lounge",
        15499,
        15,
        1550,
        { mealPlan: "Breakfast included" },
      ),
    ],
  },
];

export type HotelDeal = {
  id: string;
  title: string;
  description: string;
  eligibility: string;
  badge: string;
  hotelId?: string;
};

export const MOCK_HOTEL_DEALS: HotelDeal[] = [
  {
    id: "deal-1",
    title: "Gold member exclusive rates",
    description: "Save extra on selected premium stays when you hold Gold or above.",
    eligibility: "Gold & above",
    badge: "Tier benefit",
    hotelId: "htl-1",
  },
  {
    id: "deal-2",
    title: "Extra points on heritage stays",
    description: "Earn bonus points on eligible Jaipur hotels.",
    eligibility: "All tiers",
    badge: "Earn more",
    hotelId: "htl-3",
  },
  {
    id: "deal-3",
    title: "Limited-time city discount",
    description: "Member-only discount on selected Mumbai stays this season.",
    eligibility: "Silver & above",
    badge: "Limited time",
    hotelId: "htl-2",
  },
];

export function getHotelById(id: string) {
  return MOCK_HOTELS.find((h) => h.id === id) ?? null;
}

export function nightsBetween(checkIn: string, checkOut: string) {
  if (!checkIn || !checkOut) return 0;
  const start = new Date(checkIn);
  const end = new Date(checkOut);
  const diff = end.getTime() - start.getTime();
  if (Number.isNaN(diff) || diff <= 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export function memberNightlyPrice(hotel: MockHotel) {
  const discount = hotel.pricePerNight * (hotel.memberDiscountPct / 100);
  return Math.round(hotel.pricePerNight - discount);
}

export function formatPtsPrice(amount: number) {
  return `${Math.round(amount).toLocaleString("en-IN")} pts`;
}
